
import { Component, Vue } from 'vue-property-decorator'
import ProjectForm from '@/components/project/ProjectForm.vue'
import GroupSelector from '@/components/generic/GroupSelector.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import catalogsService from '@/services/CatalogsService'
import userInfoStore from '@/store/userInfo'
import { CrudActions, CatalogGridItemColumn, Group, BasicEntity, DomainCatalogFilters } from '@/types/Catalogs'
import { ProjectCatalogGridItem, ProjectCatalogFilters, Project } from '@/types/catalogs/Projects'
import { PageRequest } from '@/types/Support'
import { PermissionNames } from '@/types/Auth'
import { GroupEntity, GroupCatalogFilters, GroupStatus } from '@/types/catalogs/Groups'
import InputSelectorFilter from '@/components/generic/InputSelectorFilter.vue'
import DateFormat from '@/services/DateFormat'

@Component({
  components: {
    ProjectForm,
    GroupSelector,
    TopToolBarPaginator,
    MessageError,
    InputSelectorFilter
  }
})
export default class ProjectProjects extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  // Grid columns definition
  projectColumns: CatalogGridItemColumn[] = [
    CatalogGridItemColumn.def('id', 'ID', true),
    CatalogGridItemColumn.def('name', 'Nombre', true),
    CatalogGridItemColumn.def('enable', 'Habilitado', true),
    CatalogGridItemColumn.def('groups', 'Grupos', false),
    CatalogGridItemColumn.def('creationDate', 'Fecha de creación', false),
    CatalogGridItemColumn.def('updateDate', 'Fecha de actualización', false)
  ]
  items: ProjectCatalogGridItem[] = []
  filteredItems: ProjectCatalogGridItem[] = []
  /** Filters */
  filters: ProjectCatalogFilters = new ProjectCatalogFilters()
  selectedItem: ProjectCatalogGridItem = new ProjectCatalogGridItem()
  searchBy = ''
  userGroups : Group[] = []
  selectedGroup : Group = new Group()
  selectedGroupId: number = 0

  loadingDomains: boolean = false
  domains: BasicEntity[] = []
  domainSelected: BasicEntity = new BasicEntity(-1, '')

  creationDate = ''
  updateDate = ''

  created () {
    if (this.canUseDomainFilter) {
      this.setDomainSelected(userInfoStore.userInfo.domain)
      this.loadDomains(this.prepareDomainRequest())
    }
    this.init()
  }

  /** Loads the project records and the group records */
  init () {
    this.loadData()
    this.loadGroups()
  }

  /* User permissions */

  /**
   * Checks if the user has 'permission' on its permissions
   */
  hasPermission (permission: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permission)
  }
  /** Validates if the user can use the filter by domain */
  get canUseDomainFilter () {
    return userInfoStore.inRootDomain() && this.hasPermission(PermissionNames.DOMAINS_READ)
  }
  /** @returns the amount of current items */
  get totalItems () {
    return this.filteredItems.length
  }

  /* Load data */

  preRequest () {
    let pageRequest = new PageRequest<ProjectCatalogFilters>()
    pageRequest.filters.loadRelations = true
    if (this.canUseDomainFilter) {
      pageRequest.filters.domain = this.domainSelected.id
    }
    return pageRequest
  }

  /**
   * Clear and update all projects records
   */
  loadData () {
    if (this.loadingData) return
    this.loadingData = true
    this.items = []
    this.filters = new ProjectCatalogFilters()
    let pageRequest = this.preRequest()
    this.loadPage(pageRequest, false)
  }

  /**
   * Get all project records and assign them in 'items' variable
   * @param pageRequest - configurations to request
   * @param byName - if the flag are active filters the items by a 'searchBy' value
   */
  loadPage (pageRequest: PageRequest<ProjectCatalogFilters>, byName: boolean) {
    this.loadingData = true
    catalogsService.getProjects(pageRequest).then(response => {
      this.items.push(...response.results.map((it: Project): ProjectCatalogGridItem => {
        return this.fillGridItem(it, new ProjectCatalogGridItem())
      }))
      if (this.items.length < response.total) {
        pageRequest.page++
        this.loadPage(pageRequest, byName)
      } else {
        this.filteredItems = this.items
      }
    }).catch((error) => {
      let errorProps = {
        title: 'Error al cargar proyectos.',
        message: '',
        error: error,
        delay: 5000
      }
      let errorTemplate = new MessageError({ propsData: errorProps })
      errorTemplate.displayMessage()
      this.loadingData = false
    }).finally(() => {
      this.loadingData = false
      // Filter the items by a name
      if (byName) {
        this.filterItemsByName(this.searchBy)
      }
    })
  }

  /**
   * Gets all groups that can access the requesting user
   */
  async loadGroups () {
    let pageRequest = new PageRequest<GroupCatalogFilters>(this.PAGE_SIZE)
    if (this.canUseDomainFilter) {
      pageRequest.filters.domain = this.domainSelected.id
    }
    this.userGroups = []
    catalogsService.getGroups(pageRequest)
      .then((response) => {
        this.userGroups.push(...response.results.map((it: GroupEntity): Group => {
          return {
            id: it.id,
            name: it.name,
            subGroups: it.subGroups as Group[],
            status: it.status as GroupStatus,
            _selected: false } as Group
        }))
      })
  }

  /**
   * Gets all the domain records by the api users
   *
   * @param pageRequest the configurations for the request
   */
  loadDomains (pageRequest: PageRequest<DomainCatalogFilters>) {
    this.loadingDomains = true
    catalogsService.getDomains(pageRequest)
      .then((response) => {
        this.domains.push(
          ...response.results.map((domain) => {
            return new BasicEntity(domain.id, domain.name)
          }))
        if (this.domains.length < response.total) {
          pageRequest.page++
          this.loadDomains(pageRequest)
        }
      })
      .catch((error) => {
        let props = {
          title: 'Error al cargar los dominios.',
          message: '',
          delay: 5000,
          error: error
        }
        new MessageError({ propsData: props }).displayMessage()
      })
      .finally(() => { this.loadingDomains = false })
  }

  /**
   * @returns a configuration to request the domain records
   */
  prepareDomainRequest () {
    let pageRequest = new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE)
    pageRequest.sort = 'name'
    return pageRequest
  }

  /**
   * Sets the domain selected from the filter component
   * as the domain selected. This is call by the emit from the component.
   *
   * @param domain selected in the filter component
   */
  updateDomain (domain: BasicEntity) {
    if (domain.id !== this.domainSelected.id) {
      this.setDomainSelected(domain)
      this.init()
    }
  }

  /**
   * Sets the domainSelected variable with the 'domain' value
   *
   * @param domain selected
   */
  setDomainSelected (domain: BasicEntity) {
    this.domainSelected = domain
  }
  /**
   * Last selected group on tree groups
   * @param group - group selected in filters
   */
  selectGroup (group: Group) {
    if (this.selectedGroupId === group.id) {
      this.cleanSelectedGroup()
    } else {
      this.selectedGroup = group
      this.selectedGroupId = group.id
    }
  }

  /**
   * Resets project value */
  cleanSelectedItem () {
    this.selectedItem = new ProjectCatalogGridItem()
  }

  /**
   * Reset selected group item and id */
  cleanSelectedGroup () {
    this.selectedGroup = new Group()
    this.selectedGroupId = 0
    this.filters.deepSearch = false
  }

  /* Grid and action events */

  /**
   * Select the current table item on double clic.
   * @param item - row selected on table
   */
  selectItemAndShowDetail (item: any) {
    this.currentAction = CrudActions.READ_DETAIL
    this.selectItem(item)
  }

  /**
   * Select the current item and assign it in 'selectedItem' variable.
   * @param item - row selected on table
   */
  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.hasPermission(PermissionNames.PROJECTS_UPDATE) ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  /**
   * Check if 'item' is the current item selected
   * @param item - row table
   */
  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  /**
   * This triggers a form view transition event to read project
   */
  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  /**
   * This triggers a form view transition event to create project
   */
  newItem () {
    this.selectedItem = new ProjectCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  /**
   * This triggers a form view transition event to update project
   */
  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  /**
   * Create a ProjectCatagolGridItem with base on a Project object
   * @param origin - from where get values to display
   * @param target - ProjectCatalogGridItem to fill with origin object values
   */
  fillGridItem (origin: Project, target: ProjectCatalogGridItem) {
    target.id = origin.id
    target.name = origin.name
    target.domain = origin.domain
    target.enable = origin.enable ? 'Sí' : 'No'
    target.groups = origin.groups && origin.groups.length ? origin.groups.map(g => g.name).join(', ') : ''
    target.creationDate = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.updateDate = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    return target
  }

  /**
   * Switch to edit mode
   */
  modifyItem () {
    this.currentAction = CrudActions.UPDATE
  }

  /* Filter and sorting data */

  /**
   * Checks if the current row index must be render on the table data
   * @param rowIndex - to verify
   */
  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  /**
   * Sort row for a column specified
   * @param col - with witch are going to order the rows
   */
  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.filteredItems, col)
  }

  /**
   * Calculates new index range to display rows
   * @param page - current selected on paginator
   */
  onPageChange (page: number) {
    this.itemsVisibleRange = [(page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS, (page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS + this.PAGE_SIZE_VISIBLE_ITEMS]
    this.cleanSelectedItem()
  }

  /**
   * Clear paginator values
   */
  resetPaginator () {
    this.itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
    this.paginatorKey = new Date().getTime()
    this.cleanSelectedItem()
  }

  /**
   * Clear current filter configurations
   */
  cleanFilter () {
    this.filters = new ProjectCatalogFilters()
    this.filteredItems = this.items
    this.searchBy = ''
    this.resetPaginator()
    this.cleanSelectedGroup()
  }

  /**
   * Apply all filters
   */
  filterItems () {
    let filter = this.filters
    let request = this.preRequest()
    request.filters.enable = (filter.enableSelect && filter.enableSelect.length === 1) ? filter.enableSelect[0] : undefined
    request.filters.group = (this.selectedGroup && this.selectedGroupId) ? this.selectedGroupId : 0
    request.filters.deepSearch = this.selectedGroupId ? filter.deepSearch : false
    this.items = []
    this.loadPage(request, true)
  }

  /**
   * Filters the current items by name
   */
  filterItemsByName (search: string) {
    let regexSearch = new RegExp(search, 'i')
    this.filteredItems = this.items.filter(it => !search || regexSearch.test(it.name))
  }

  /* Form */

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  /**
   * Update row fields when an item was saved
   * @param item
   */
  onSaveItem (item: Project) {
    if (!this.selectedItem.id) {
      this.selectedItem = new ProjectCatalogGridItem()
      this.selectedItem.id = item.id
      this.items.unshift(this.selectedItem)
    }
    this.fillGridItem(item, this.selectedItem)
    this.currentAction = CrudActions.READ
    this.loadData()
  }

  /* Export data */

  printCsv () {
    let csv = ''
      .concat('ID').concat(',')
      .concat('NOMBRE').concat(',')
      .concat('DOMINIO').concat(',')
      .concat('HABILITADO').concat(',')
      .concat('GRUPOS').concat(',')
      .concat('CREACION').concat(',')
      .concat('ACTUALIZACION').concat('\n')

    for (let it of this.filteredItems) {
      csv += ''
        .concat(it.id.toString()).concat(',')
        .concat(it.name).concat(',')
        .concat(it.domain.name).concat(',')
        .concat(it.enable.toString()).concat(',')
        .concat(it.groups.length.toString()).concat(',')
        .concat(it.creationDate).concat(',')
        .concat(it.updateDate).concat('\n')
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `Projects.csv`
    element.click()
  }
}
